import type { Medal, MedalTallyCell, MedalTallyColumn, MedalTallyRow } from "@seagames/contracts";

/**
 * The tally page's arithmetic over rows the API already ranked — pure, tested
 * (`tally.test.ts`). The API's rank (gold, silver, bronze; ties share) is the
 * standing; a colour sort only reorders the rows on screen.
 */

export interface TallyTotals {
	gold: number;
	silver: number;
	bronze: number;
	total: number;
}

export function tallyTotals(rows: readonly Pick<MedalTallyRow, "gold" | "silver" | "bronze" | "total">[]): TallyTotals {
	return rows.reduce((t, r) => ({ gold: t.gold + r.gold, silver: t.silver + r.silver, bronze: t.bronze + r.bronze, total: t.total + r.total }), {
		gold: 0,
		silver: 0,
		bronze: 0,
		total: 0,
	});
}

/** Ranks held by more than one row — printed "=2". */
export function sharedRanks(rows: readonly Pick<MedalTallyRow, "rank">[]): Set<number> {
	const seen = new Map<number, number>();
	for (const r of rows) seen.set(r.rank, (seen.get(r.rank) ?? 0) + 1);
	return new Set([...seen].filter(([, n]) => n > 1).map(([rank]) => rank));
}

/** How the table may be ordered: the standing, or most of one colour / of all. */
export const TALLY_SORTS = ["rank", "gold", "silver", "bronze", "total"] as const;
export type TallySort = (typeof TALLY_SORTS)[number];

const KEY: Record<Exclude<TallySort, "rank">, keyof TallyTotals> = { gold: "gold", silver: "silver", bronze: "bronze", total: "total" };

/** The rows in the asked order; ties keep the API's order (the standing, then NOC code). */
export function sortTally<R extends MedalTallyRow>(rows: readonly R[], sort: TallySort): R[] {
	if (sort === "rank") return [...rows];
	const k = KEY[sort];
	return rows
		.map((r, i) => ({ r, i }))
		.sort((a, b) => b.r[k] - a.r[k] || a.i - b.i)
		.map((x) => x.r);
}

/** The rows that lead: rank 1 — several when tied. Empty while no medal is awarded. */
export function tallyLeaders<R extends MedalTallyRow>(rows: readonly R[]): R[] {
	if (!rows.some((r) => r.total > 0)) return [];
	return rows.filter((r) => r.rank === 1);
}

/** The rows with the most of one colour (ties included). Empty when nobody has one. */
export function colourLeaders<R extends MedalTallyRow>(rows: readonly R[], medal: Medal): R[] {
	const k = medal === "GOLD" ? "gold" : medal === "SILVER" ? "silver" : "bronze";
	const best = Math.max(0, ...rows.map((r) => r[k]));
	return best > 0 ? rows.filter((r) => r[k] === best) : [];
}

/** One row's cell of a breakdown column, zeros when it has none. */
export function cellOf(row: Pick<MedalTallyRow, "cells">, key: string): MedalTallyCell {
	return row.cells?.find((c) => c.key === key) ?? { key, gold: 0, silver: 0, bronze: 0, total: 0 };
}

export interface ColumnLeader<R> {
	column: MedalTallyColumn;
	/** The rows leading that column (gold, silver, bronze — ties included). */
	leaders: R[];
	total: number;
}

/**
 * Per breakdown column (a sport, a day): who leads it by the tally's rule
 * (gold, then silver, then bronze; ties share) and how many medals it gave.
 * Columns with no medal are left out.
 */
export function columnLeaders<R extends MedalTallyRow>(rows: readonly R[], columns: readonly MedalTallyColumn[]): ColumnLeader<R>[] {
	const out: ColumnLeader<R>[] = [];
	for (const column of columns) {
		const cells = rows.map((r) => ({ r, c: cellOf(r, column.key) })).filter((x) => x.c.total > 0);
		if (cells.length === 0) continue;
		const better = (a: MedalTallyCell, b: MedalTallyCell) => a.gold - b.gold || a.silver - b.silver || a.bronze - b.bronze;
		const best = cells.reduce((m, x) => (better(x.c, m) > 0 ? x.c : m), cells[0]!.c);
		out.push({
			column,
			leaders: cells.filter((x) => better(x.c, best) === 0).map((x) => x.r),
			total: cells.reduce((n, x) => n + x.c.total, 0),
		});
	}
	return out;
}

// ─── CSV ─────────────────────────────────────────────────────────────────────

/** The UTF-8 byte order mark, as a code point (never a literal invisible character in source). */
export const BOM = String.fromCharCode(0xfeff);

/**
 * One CSV field: quoted when it holds a comma, quote or line break (quotes
 * doubled), and a leading `=`, `+`, `-`, `@`, tab or CR prefixed with `'` so a
 * spreadsheet never runs a NOC name as a formula (CSV injection).
 */
export function csvField(value: string | number | null | undefined): string {
	let s = value === null || value === undefined ? "" : String(value);
	if (typeof value === "string" && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
	return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export interface TallyCsvOptions {
	/** The heading lines above the table ("Medal tally", the games, "as of …"), one field each. */
	heading: readonly string[];
	/** The column titles: rank, NOC code, NOC, gold, silver, bronze, total. */
	head: readonly [string, string, string, string, string, string, string];
	/** With a breakdown: a G / S / B / total group per column, after the totals. */
	columns?: readonly MedalTallyColumn[];
	/** Ranks held by several rows: printed "2=" (a leading "=" would read as a formula). */
	shared?: ReadonlySet<number>;
}

/**
 * The tally as a CSV file's text — UTF-8 with a BOM (so a spreadsheet opens
 * Thai or Khmer names right), CRLF line ends, every field through `csvField`.
 * Built from the rows the page already holds: no request of its own.
 */
export function tallyCsv(rows: readonly MedalTallyRow[], o: TallyCsvOptions): string {
	const lines: string[] = [];
	for (const h of o.heading) lines.push(csvField(h));
	if (o.heading.length > 0) lines.push("");
	const extra = (o.columns ?? []).flatMap((c) => [`${c.name} G`, `${c.name} S`, `${c.name} B`, `${c.name} total`]);
	lines.push([...o.head, ...extra].map(csvField).join(","));
	for (const r of rows) {
		const rank = o.shared?.has(r.rank) ? `${r.rank}=` : r.rank;
		const cells = (o.columns ?? []).flatMap((c) => {
			const cell = cellOf(r, c.key);
			return [cell.gold, cell.silver, cell.bronze, cell.total];
		});
		lines.push([rank, r.noc.code ?? "", r.noc.country_name ?? r.noc.name, r.gold, r.silver, r.bronze, r.total, ...cells].map(csvField).join(","));
	}
	return `${BOM}${lines.join("\r\n")}\r\n`;
}
